import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import {
  compose,
  setDisplayName,
  setPropTypes,
  branch,
  renderComponent,
  renderNothing,
  lifecycle,
  withState,
} from 'recompose';
import { withBreakpoint } from '../../enhancers';
import Spinner from '../common/Spinner';
import LogoutButton from '../common/LogoutButton';
import Profile from './Profile';
import Subscriptions from './Subscriptions';
import { loadAccountPageRequest } from './actions';
import styles from './AccountPage.scss';
import { getTabFromHash, updateSecondaryNavActive } from '../../utils/secondaryNavUtils';

const canActivateSubscriptionsTab = userServices =>
  userServices.data
  && userServices.data.subscriptions
  && userServices.data.subscriptions.length > 0;

const HeaderPortal = ({ children }) => {
  const headerElement = document.getElementById('my-account-header');
  if (!headerElement) return null;
  return ReactDOM.createPortal(children, headerElement);
};

export const AccountPage = ({ activeTab }) => (
  <div className={classNames('ui-canvas ui-canvas--full', styles.canvas)}>
    <HeaderPortal>
      <div className={styles.header}>
        <div className={styles.headerContainer}>
          <span className={styles.headerTitle}>
            {activeTab === 'account' ? 'My Account' : 'Subscription Packages'}
          </span>
          <LogoutButton />
        </div>
      </div>
    </HeaderPortal>
    <section className={styles.root}>
      {activeTab === 'account' && <Profile />}
      {activeTab === 'subscriptions' && <Subscriptions />}
    </section>
  </div>
);

export default compose(
  setDisplayName('AccountPage'),
  connect(
    ({ accountPage, userInfo, userServices }) => ({ accountPage, userInfo, userServices }),
    { loadAccountPageRequest },
  ),
  withBreakpoint,
  withState('activeTab', 'setActiveTab', 'account'),
  lifecycle({
    componentDidMount() {
      this.props.loadAccountPageRequest();

      const tab = getTabFromHash();
      if (tab !== this.props.activeTab) {
        this.props.setActiveTab(tab);
      }

      updateSecondaryNavActive();

      this.handleHashChange = () => {
        const newTab = getTabFromHash();
        if (newTab === 'subscriptions' && !canActivateSubscriptionsTab(this.props.userServices)) {
          global.location.assign('/subscriptions');
          return;
        }
        if (newTab !== this.props.activeTab) {
          this.props.setActiveTab(newTab);
        }
        updateSecondaryNavActive();
      };
      window.addEventListener('hashchange', this.handleHashChange);
    },
    componentDidUpdate(prevProps) {
      if (prevProps.accountPage.fetching && !this.props.accountPage.fetching
        && !canActivateSubscriptionsTab(this.props.userServices)
        && this.props.activeTab === 'subscriptions') {
        global.location.assign('/subscriptions');
      }
    },
    componentWillUnmount() {
      window.removeEventListener('hashchange', this.handleHashChange);
    },
  }),
  branch(
    props => props.accountPage.fetching,
    renderComponent(Spinner),
  ),
  branch(
    props => props.accountPage.error,
    renderNothing,
  ),
  setPropTypes({
    accountPage: PropTypes.shape({
      error: PropTypes.bool,
      fetching: PropTypes.bool,
    }).isRequired,
    userInfo: PropTypes.shape({
      data: PropTypes.any,
      error: PropTypes.any,
      fetching: PropTypes.bool,
    }).isRequired,
    userServices: PropTypes.shape({
      data: PropTypes.any,
      error: PropTypes.any,
      fetching: PropTypes.bool,
    }).isRequired,
  }),
)(AccountPage);
